import type { ChecklistItem, ChecklistWorkflowStatus } from '../../types/schema'
import type { MemberBoardSubTab } from './MemberBoardHub'
import {
  getMemberReviewReadinessLabel,
  getMemberStatusFilterMeta,
  type MobileNlsT,
} from '../Mobile/MobileNls'

export type MemberBoardStatusFilter = 'ALL' | ChecklistWorkflowStatus
export type MemberReviewReadiness = 'READY' | 'EVIDENCE' | 'LOCATION' | 'CO_SIGN'

export const CHECKLIST_WORKFLOW_STATUSES: Array<{
  id: ChecklistWorkflowStatus
  label: string
  hint: string
}> = [
  { id: 'CREATED', label: 'Created', hint: 'Not assigned yet' },
  { id: 'ASSIGNED', label: 'Assigned', hint: 'Waiting for acceptance' },
  { id: 'ACCEPTED', label: 'Accepted', hint: 'Ready to start' },
  { id: 'ONGOING', label: 'Ongoing', hint: 'Currently moving' },
  { id: 'ASSESSING', label: 'Assessing', hint: 'Ready for review' },
  { id: 'COMPLETED', label: 'Completed', hint: 'Finished work' },
  { id: 'NOT_REQUIRED', label: 'Not Required', hint: 'Skipped by rule' },
  { id: 'POSTPONED', label: 'Postponed', hint: 'Deferred work' },
]

export const MEMBER_REVIEW_READINESS_LABEL: Record<MemberReviewReadiness, string> = {
  READY: 'Ready',
  EVIDENCE: 'Evidence',
  LOCATION: 'Location',
  CO_SIGN: 'Co-sign',
}

export const getLocalizedMemberReviewReadinessLabel = (
  readiness: MemberReviewReadiness,
  t?: MobileNlsT,
): string => getMemberReviewReadinessLabel(readiness, t)

export const getLocalizedMemberBoardStatusFilterMeta = (
  status: MemberBoardStatusFilter,
  t?: MobileNlsT,
): { label: string; hint: string } => getMemberStatusFilterMeta(status, t)

export const normalizeStripboardStatus = (item: ChecklistItem): ChecklistWorkflowStatus =>
  item.workflowStatus ?? (item.isCompleted ? 'COMPLETED' : item.assigneeId ? 'ASSIGNED' : 'CREATED')

export const compareChecklistItems = (left: ChecklistItem, right: ChecklistItem) => {
  const priorityDelta = (right.priority ?? 0) - (left.priority ?? 0)
  if (priorityDelta) return priorityDelta
  const leftDue = left.scheduleEndDate ?? left.endDate ?? left.scheduleStartDate ?? left.startDate ?? '9999-12-31'
  const rightDue = right.scheduleEndDate ?? right.endDate ?? right.scheduleStartDate ?? right.startDate ?? '9999-12-31'
  return leftDue.localeCompare(rightDue) || left.title.localeCompare(right.title)
}

export const isSyntheticDashboardChecklistParent = (parentContainerId?: string | null): boolean =>
  parentContainerId === 'workspace-default-checklist'
  || Boolean(parentContainerId && /^board-root:.*:DASHBOARD$/.test(parentContainerId))

const RECURRING_SUFFIX_RE = /_(\d{4}-\d{2}-\d{2}|\d{4}-W\d{2})$/

export const getMemberBoardCardTitle = (item: ChecklistItem): string => {
  const title = item.title?.trim() || 'Untitled checklist'
  if (item.occurrenceKey && title.endsWith(`_${item.occurrenceKey}`)) {
    return title.slice(0, -(`_${item.occurrenceKey}`).length)
  }
  if (item.isRecurrenceInstance === true || item.checklistType === 'DAILY' || item.checklistType === 'WEEKLY') {
    return title.replace(RECURRING_SUFFIX_RE, '')
  }
  return title
}

export const getMemberBoardParentLabel = (
  item: ChecklistItem,
  parentTitle: string | null,
): string => {
  if (parentTitle) return parentTitle
  return isSyntheticDashboardChecklistParent(item.parentContainerId) ? 'Board checklist' : 'Workspace checklist'
}

export const getMemberReviewTimestamp = (item: ChecklistItem): number => {
  const raw = item.proofUploadedAt ?? item.scheduleEndDate ?? item.endDate ?? item.scheduleStartDate ?? item.startDate
  if (!raw) return 0
  const parsed = Date.parse(raw)
  return Number.isNaN(parsed) ? 0 : parsed
}

export const formatMemberReviewDate = (value?: string | null): string => {
  if (!value) return 'No due date'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return 'No due date'
  return parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

export const getMemberReviewReadiness = (item: ChecklistItem): MemberReviewReadiness => {
  if (item.requiresProofFile && !item.proofFileUrl && !item.proofSubmissionId) return 'EVIDENCE'
  if (item.requiresLocationPin && !item.verifiedCoordinates) return 'LOCATION'
  if (item.requiresCoSign && !item.coSignedByUid) return 'CO_SIGN'
  return 'READY'
}

export const filterChecklistItemsForStripboard = (
  items: readonly ChecklistItem[],
  workspaceId: string,
  statusFilter: MemberBoardStatusFilter,
): ChecklistItem[] => items
  .filter((item) => item.workspaceId === workspaceId)
  .filter((item) => (item.board ?? 'DASHBOARD') === 'DASHBOARD')
  .filter((item) => statusFilter === 'ALL' || normalizeStripboardStatus(item) === statusFilter)
  .sort(compareChecklistItems)

export const MEMBER_BOARD_SUB_TABS: readonly MemberBoardSubTab[] = [
  'STRIPBOARD',
  'MANAGE',
  'FOLDER',
  'MEETINGS',
  'ANALYSIS',
] as const

export const migrateMemberBoardSubValue = (value: unknown): MemberBoardSubTab | null => {
  if (value === 'DASHBOARD') return 'STRIPBOARD'
  if (typeof value === 'string' && (MEMBER_BOARD_SUB_TABS as readonly string[]).includes(value)) {
    return value as MemberBoardSubTab
  }
  return null
}

export const migrateMemberBoardSub = (): MemberBoardSubTab => {
  const fallback: MemberBoardSubTab = 'STRIPBOARD'
  if (typeof window === 'undefined') return fallback

  const migrateRawValue = (raw: string | null): string | null => {
    if (raw == null) return null
    try {
      const parsed = JSON.parse(raw) as unknown
      const migrated = migrateMemberBoardSubValue(parsed)
      return migrated ? JSON.stringify(migrated) : null
    } catch {
      const migrated = migrateMemberBoardSubValue(raw)
      return migrated
    }
  }

  try {
    const localRaw = window.localStorage.getItem('member.boardSub')
    const localMigrated = migrateRawValue(localRaw)
    if (localMigrated !== null && localMigrated !== localRaw) {
      window.localStorage.setItem('member.boardSub', localMigrated)
    }
  } catch {
    // Storage can be blocked in private contexts.
  }

  try {
    const sessionKeys = Array.from({ length: window.sessionStorage.length }, (_, index) => window.sessionStorage.key(index))
      .filter((key): key is string => Boolean(key && (key === 'member.boardSub' || key.endsWith(':member.boardSub'))))
    for (const key of sessionKeys) {
      const raw = window.sessionStorage.getItem(key)
      const migrated = migrateRawValue(raw)
      if (migrated !== null && migrated !== raw) window.sessionStorage.setItem(key, migrated)
    }
  } catch {
    // Storage can be blocked in private contexts.
  }

  return fallback
}
